import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Box,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import api from '../services/api';

interface JurisdictionCheck {
  state: string | null;
  is_flagged: boolean;
  risk_level: string | null;
  warning_text: string | null;
  acknowledged: boolean;
}

const JurisdictionWarningModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<JurisdictionCheck | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    const checkJurisdiction = async () => {
      try {
        const res = await api.get('/api/v1/jurisdiction/check');
        const check: JurisdictionCheck = res.data;
        if (check.is_flagged && !check.acknowledged) {
          setData(check);
          setOpen(true);
        }
      } catch {
        // Silently fail — modal is non-blocking
      }
    };
    checkJurisdiction();
  }, []);

  const handleAcknowledge = async () => {
    try {
      setAcknowledging(true);
      await api.post('/api/v1/jurisdiction/acknowledge');
      setOpen(false);
    } catch {
      // Silently fail
    } finally {
      setAcknowledging(false);
    }
  };

  if (!data) return null;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          border: '2px solid #E8A045',
          backgroundColor: '#FFFAF0',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon sx={{ color: '#E8A045' }} />
        Jurisdiction Notice — {data.state}
        <Chip
          label={data.risk_level?.toUpperCase()}
          size="small"
          sx={{
            ml: 1,
            backgroundColor: data.risk_level === 'high' ? '#C0392B' : '#E8A045',
            color: '#fff',
            fontWeight: 600,
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, color: '#5A4A3A' }}>
          {data.warning_text}
        </Typography>
        <Box
          sx={{
            p: 2,
            backgroundColor: 'rgba(232,160,69,0.1)',
            borderRadius: 1,
            border: '1px solid rgba(232,160,69,0.3)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#7A5F4E' }}>
            By clicking "I Understand" you acknowledge that you have been informed of the
            potential legal risks in your jurisdiction. Quantato does not provide legal advice.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleAcknowledge}
          variant="contained"
          disabled={acknowledging}
          sx={{
            backgroundColor: '#3A8C8C',
            '&:hover': { backgroundColor: '#2D6F6F' },
          }}
        >
          {acknowledging ? 'Acknowledging...' : 'I Understand'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JurisdictionWarningModal;
